import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const EventAdmin = ({ page = 'event_list', onNavigate }) => {
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // イベント作成
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', location: '', memo: '' });

  // イベント詳細
  const [selectedEvent, setSelectedEvent] = useState(null);

  // インポート
  const [importPreview, setImportPreview] = useState(null);

  // 参加者追加
  const [addMemberSearch, setAddMemberSearch] = useState('');

  // 参加者編集モーダル
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [seminars, setSeminars] = useState([]);

  // 参加者への一斉送信（手動）
  const [composeShow, setComposeShow] = useState(false);
  const [composeFrom, setComposeFrom] = useState('SrWith株式会社 事務局');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSign, setComposeSign] = useState(true);
  const [composeSending, setComposeSending] = useState(false);

  // 自動リマインド設定（イベントID→設定）
  const [reminders, setReminders] = useState({});
  const [reminderModalId, setReminderModalId] = useState(null);
  const [reminderForm, setReminderForm] = useState(null);

  const saveReminders = async (data) => {
    try {
      await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'event-reminders', data }) });
      setReminders(data);
    } catch {}
  };

  const openReminder = (eventId) => {
    const r = reminders[eventId] || {};
    setReminderForm({
      enabled: !!r.enabled,
      daysBefore: r.daysBefore ?? 1,
      fromName: r.fromName || 'SrWith株式会社 事務局',
      subject: r.subject || '',
      body: r.body || '',
      sign: r.sign !== false,
      sentAt: r.sentAt || null,
    });
    setReminderModalId(eventId);
  };

  const saveReminderForm = () => {
    if (reminderForm.enabled && (!reminderForm.subject.trim() || !reminderForm.body.trim())) { setMessage('リマインドの件名と本文を入力してください'); return; }
    const next = { ...reminders, [reminderModalId]: {
      enabled: reminderForm.enabled,
      daysBefore: Number(reminderForm.daysBefore) || 1,
      fromName: reminderForm.fromName,
      subject: reminderForm.subject,
      body: reminderForm.body,
      sign: reminderForm.sign,
      sentAt: reminderForm.sentAt || null,
    } };
    saveReminders(next);
    setReminderModalId(null);
    setMessage('リマインド設定を保存しました');
  };

  const sendToParticipants = async (memberEmails) => {
    if (!composeSubject.trim() || !composeBody.trim()) { setMessage('件名と本文を入力してください'); return; }
    const emails = (memberEmails || []).filter(Boolean);
    if (emails.length === 0) { setMessage('参加者がいません'); return; }
    if (!window.confirm(`参加者 ${emails.length}名に送信登録します。よろしいですか？（サーバーで送信・この画面を閉じてもOK）`)) return;
    setComposeSending(true);
    try {
      const fd = new FormData();
      fd.append('subject', composeSubject);
      fd.append('body', composeBody);
      fd.append('fromName', composeFrom);
      fd.append('appendSignature', composeSign ? '1' : '0');
      fd.append('emails', JSON.stringify(emails));
      const res = await adminFetch('/api/admin-ml.php?action=enqueue', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        setMessage(`参加者への送信を登録しました（${data.total}名）。サーバーがバックグラウンドで送信します（画面を閉じてもOK）。`);
        adminFetch('/api/admin-ml.php?action=process').catch(() => {});
        setComposeShow(false); setComposeSubject(''); setComposeBody('');
      } else {
        setMessage('送信登録に失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('送信登録に失敗しました');
    } finally {
      setComposeSending(false);
    }
  };

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=events&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setEvents).catch(() => setEvents([]));
    adminFetch('/api/admin-read.php?file=customers&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setCustomers).catch(() => setCustomers([]));
    adminFetch('/api/admin-read.php?file=seminars&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setSeminars).catch(() => setSeminars([]));
    adminFetch('/api/admin-read.php?file=event-reminders&t=' + Date.now()).then((r) => r.ok ? r.json() : {}).then((d) => setReminders(d && typeof d === 'object' && !Array.isArray(d) ? d : {})).catch(() => setReminders({}));
  }, []);

  const saveEvents = async (data) => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'events', data }) });
      const result = await res.json();
      if (result.success) setEvents(data);
    } catch {}
    setSaving(false);
  };

  const saveCustomers = async (data) => {
    try {
      const res = await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'customers', data }) });
      const result = await res.json();
      if (result.success) setCustomers(data);
    } catch {}
  };

  const getField = (c, key) => (c.fields || []).find((f) => f.key === key)?.value || '';

  const createEvent = () => {
    if (!form.name.trim()) return;
    const newEvent = { id: String(Date.now()), ...form, createdAt: new Date().toISOString() };
    saveEvents([...events, newEvent]);
    setForm({ name: '', date: '', location: '', memo: '' });
    setShowForm(false);
    setMessage(`「${newEvent.name}」を作成しました`);
  };

  const deleteEvent = (id) => {
    if (!window.confirm('このイベントを削除しますか？')) return;
    saveEvents(events.filter((e) => e.id !== id));
    if (selectedEvent?.id === id) setSelectedEvent(null);
    setMessage('削除しました');
  };

  // イベントに紐づく顧客
  const getEventCustomers = (eventId) => customers.filter((c) => getField(c, 'イベントID') === eventId);

  // イベント内のMLパターン一覧
  const getEventPatterns = (eventId) => {
    const members = getEventCustomers(eventId);
    const map = {};
    members.forEach((c) => {
      const p = getField(c, 'MLパターン');
      if (p) {
        if (!map[p]) map[p] = 0;
        map[p]++;
      }
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  };

  // Excelインポート
  const handleExcelFile = async (e, eventId) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('action', 'preview');
    formData.append('eventId', eventId);
    try {
      const res = await adminFetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setImportPreview({ data: data.parsed, totalCount: data.totalCount, skippedList: data.skipped, fileName: file.name, eventId, file });
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    e.target.value = '';
  };

  const executeImport = async () => {
    if (!importPreview) return;
    const formData = new FormData();
    formData.append('file', importPreview.file);
    formData.append('action', 'import');
    formData.append('eventId', importPreview.eventId);
    try {
      const res = await adminFetch('/api/admin-import.php', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.importedCount}件をインポートしました${data.skippedCount > 0 ? `（重複スキップ: ${data.skippedCount}件）` : ''}`);
        adminFetch('/api/admin-read.php?file=customers&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setCustomers);
      } else {
        setMessage(data.message || 'インポートに失敗しました');
      }
    } catch { setMessage('インポートに失敗しました'); }
    setImportPreview(null);
  };

  // 参加者手動追加
  const addMemberToEvent = async () => {
    if (!addMemberSearch.trim()) return;
    const email = addMemberSearch.trim();
    if (!email.includes('@')) return;

    const currentId = localStorage.getItem('selected_event_id') || '';
    const existing = customers.find((c) => c.email?.toLowerCase() === email.toLowerCase());

    if (existing) {
      // 既存顧客にイベントIDを紐づけ
      const hasEvent = (existing.fields || []).some((f) => f.key === 'イベントID' && f.value === currentId);
      if (!hasEvent) {
        const updated = customers.map((c) => {
          if (c.id !== existing.id) return c;
          return { ...c, fields: [...(c.fields || []), { key: 'イベントID', value: currentId }] };
        });
        await saveCustomers(updated);
      }
    } else {
      // 新規顧客登録+イベントID紐づけ
      const newCustomer = {
        id: String(Date.now()),
        name: '',
        email,
        type: 'company',
        status: '見込み',
        memo: '',
        fields: [{ key: 'イベントID', value: currentId }],
      };
      await saveCustomers([...customers, newCustomer]);
    }

    setAddMemberSearch('');
    setMessage('追加しました');
  };

  // 参加者編集
  const openEdit = (customer) => {
    setEditForm({ ...customer, fields: [...(customer.fields || [])] });
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setEditSaving(true);
    const updated = customers.map((c) => c.id === editForm.id ? editForm : c);
    await saveCustomers(updated);
    setEditForm(null);
    setMessage('保存しました');
    setEditSaving(false);
  };

  const salesStatusOptions = ['見込み', '商談中', '休止'];
  const statusColors = {
    '見込み': 'bg-yellow-100 text-yellow-700',
    '商談中': 'bg-blue-100 text-blue-700',
    '休止': 'bg-gray-100 text-gray-500',
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP');
  };

  // ===== イベント一覧 =====
  if (page === 'event_list') {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">イベント一覧</h2>
          <button onClick={() => setShowForm(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">+ 新規イベント</button>
        </div>

        {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

        {showForm && (
          <div className="bg-white rounded-xl shadow p-6 mb-4 space-y-3">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="イベント名（例: 働き方の祭典2026）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="開催場所" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <textarea value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} rows={2}
              placeholder="メモ（任意）" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            <div className="flex gap-3">
              <button onClick={createEvent} disabled={saving || !form.name.trim()} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">作成</button>
              <button onClick={() => { setShowForm(false); setForm({ name: '', date: '', location: '', memo: '' }); }} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
            </div>
          </div>
        )}

        {/* セミナー（自社主催） */}
        {seminars.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 mb-2">セミナー（自社主催）</h3>
            <div className="space-y-2">
              {seminars.map((sem, i) => {
                const semId = 'seminar_' + (sem.id || i);
                const semMembers = getEventCustomers(semId);
                return (
                  <div key={i} className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm">{sem.fullTitle || sem.shortTitle || sem.label}</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700">セミナー</span>
                      </div>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                        {sem.eventDate && <span>{sem.eventDate}</span>}
                        {sem.time && <span>{sem.time}</span>}
                        <span>{semMembers.length}名</span>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => openReminder(semId)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold ${reminders[semId]?.enabled ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                        リマインド{reminders[semId]?.enabled ? `(${reminders[semId].daysBefore}日前)` : ''}
                      </button>
                      <button onClick={() => { localStorage.setItem('selected_event_id', semId); if (onNavigate) onNavigate('event_members'); }}
                        className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">参加者管理</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* イベント（外部参加等） */}
        <div>
          <h3 className="text-sm font-bold text-gray-500 mb-2">イベント</h3>
          {events.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">イベントがありません</div>
          ) : (
            <div className="space-y-2">
              {[...events].reverse().map((ev) => {
                const members = getEventCustomers(ev.id);
                return (
                  <div key={ev.id} className="bg-white rounded-xl shadow p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-sm">{ev.name}</h3>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-green-100 text-green-700">イベント</span>
                        </div>
                        <div className="flex gap-3 mt-1 text-xs text-gray-500">
                          {ev.date && <span>{formatDate(ev.date)}</span>}
                          {ev.location && <span>{ev.location}</span>}
                          <span>{members.length}名</span>
                        </div>
                        {ev.memo && <p className="text-xs text-gray-400 mt-1">{ev.memo}</p>}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => openReminder(ev.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold ${reminders[ev.id]?.enabled ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                          リマインド{reminders[ev.id]?.enabled ? `(${reminders[ev.id].daysBefore}日前)` : ''}
                        </button>
                        <button onClick={() => { localStorage.setItem('selected_event_id', ev.id); if (onNavigate) onNavigate('event_members'); }}
                          className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-200">参加者管理</button>
                        <button onClick={() => deleteEvent(ev.id)} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-100">削除</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {reminderModalId && reminderForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setReminderModalId(null)}>
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg">自動リマインド設定</h3>
                <button onClick={() => setReminderModalId(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
              </div>
              <p className="text-xs text-gray-500 mb-3">開催日の指定日数前に、参加者へ自動でメールを送信します（毎朝チェック・サーバーが送信）。</p>
              <label className="inline-flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={reminderForm.enabled} onChange={(e) => setReminderForm((f) => ({ ...f, enabled: e.target.checked }))} />
                <span className="text-sm font-bold">自動リマインドを有効にする</span>
              </label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-600">開催の</label>
                  <input type="number" min="0" value={reminderForm.daysBefore} onChange={(e) => setReminderForm((f) => ({ ...f, daysBefore: e.target.value }))}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <span className="text-xs font-bold text-gray-600">日前に送信</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-600">差出人</label>
                  <select value={reminderForm.fromName} onChange={(e) => setReminderForm((f) => ({ ...f, fromName: e.target.value }))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="SrWith株式会社 事務局">SrWith株式会社 事務局</option>
                    <option value="社会保険労務士 塚本祥太郎">社会保険労務士 塚本祥太郎</option>
                  </select>
                </div>
                <input type="text" value={reminderForm.subject} onChange={(e) => setReminderForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="件名" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <textarea value={reminderForm.body} onChange={(e) => setReminderForm((f) => ({ ...f, body: e.target.value }))} rows={8}
                  placeholder="本文（{company} {name} が差し込めます）" className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={reminderForm.sign} onChange={(e) => setReminderForm((f) => ({ ...f, sign: e.target.checked }))} />
                  <span className="text-xs font-bold text-gray-700">末尾に署名を自動で付ける</span>
                </label>
                {reminderForm.sentAt && (
                  <div className="text-xs bg-gray-50 rounded p-2 flex items-center justify-between">
                    <span className="text-gray-500">送信済み（{formatDate(reminderForm.sentAt)}）</span>
                    <button onClick={() => setReminderForm((f) => ({ ...f, sentAt: null }))} className="text-blue-600 font-bold hover:underline">再送可能にする</button>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={saveReminderForm} className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">保存</button>
                <button onClick={() => setReminderModalId(null)} className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== 参加者管理 =====
  if (page === 'event_members') {
    const savedId = localStorage.getItem('selected_event_id');
    // セミナー+イベントを統合した選択肢
    const allItems = [
      ...seminars.map((sem, i) => ({ id: 'seminar_' + (sem.id || i), name: sem.fullTitle || sem.shortTitle || sem.label, type: 'seminar' })),
      ...events.map((e) => ({ id: e.id, name: e.name, type: 'event', date: e.date })),
    ];
    const currentId = selectedEvent?.id || savedId || (allItems.length > 0 ? allItems[allItems.length - 1].id : '');
    const currentItem = allItems.find((item) => item.id === currentId) || allItems[allItems.length - 1];
    const members = currentId ? getEventCustomers(currentId) : [];

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">参加者管理</h2>
            {currentItem && <p className="text-sm text-gray-500 mt-1">{currentItem.name}</p>}
          </div>
          <div className="flex gap-2">
              <>
                <select
                  value={currentId}
                  onChange={(e) => { localStorage.setItem('selected_event_id', e.target.value); setSelectedEvent({ id: e.target.value }); }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {seminars.length > 0 && <optgroup label="セミナー">
                    {seminars.map((sem, i) => <option key={'s'+i} value={'seminar_' + (sem.id || i)}>{sem.fullTitle || sem.shortTitle || sem.label}</option>)}
                  </optgroup>}
                  {events.length > 0 && <optgroup label="イベント">
                    {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </optgroup>}
                </select>
                <label className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-700 cursor-pointer">
                  Excelインポート
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => handleExcelFile(e, currentId)} />
                </label>
              </>
          </div>
        </div>

        {/* 手動追加 */}
        {currentId && (
          <div className="mb-4 bg-white rounded-xl shadow px-4 py-3">
            <div className="flex gap-2">
              <input type="text" value={addMemberSearch} onChange={(e) => setAddMemberSearch(e.target.value)}
                placeholder="名前・メール・会社名で検索して追加、またはメールアドレスを直接入力"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && addMemberToEvent()} />
              <button onClick={addMemberToEvent} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">追加</button>
            </div>
            {addMemberSearch.trim() && !addMemberSearch.includes('@') && (() => {
              const s = addMemberSearch.toLowerCase();
              const memberEmails = new Set(members.map((c) => c.email));
              const matches = customers.filter((c) => c.email && !memberEmails.has(c.email) &&
                (c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || getField(c, '会社名').toLowerCase().includes(s))
              ).slice(0, 8);
              return matches.length > 0 ? (
                <div className="mt-2 border border-gray-200 rounded bg-white max-h-40 overflow-y-auto">
                  {matches.map((c) => (
                    <button key={c.id} onClick={() => setAddMemberSearch(c.email)}
                      className="w-full text-left px-3 py-1.5 hover:bg-blue-50 text-xs flex items-center justify-between border-b last:border-b-0">
                      <span><span className="font-bold">{c.name}</span> <span className="text-gray-400">{getField(c, '会社名')}</span></span>
                      <span className="text-gray-400">{c.email}</span>
                    </button>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        )}

        {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

        {/* インポートプレビュー */}
        {importPreview && (
          <div className="mb-4 bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-lg mb-3">インポートプレビュー: {importPreview.fileName}</h3>
            <div className="flex gap-4 mb-3 text-sm">
              <span className="font-bold text-blue-700">取り込み: {(importPreview.totalCount || importPreview.data.length)}件</span>
              {importPreview.skippedList.length > 0 && <span className="font-bold text-orange-600">重複スキップ: {importPreview.skippedList.length}件</span>}
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-lg mb-4">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">名前</th>
                    <th className="text-left px-3 py-2">メール</th>
                    <th className="text-left px-3 py-2">会社名</th>
                    <th className="text-left px-3 py-2">MLパターン</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {importPreview.data.slice(0, 50).map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5">{c.name}</td>
                      <td className="px-3 py-1.5">{c.email}</td>
                      <td className="px-3 py-1.5">{(c.fields || []).find((f) => f.key === '会社名')?.value || ''}</td>
                      <td className="px-3 py-1.5">{(c.fields || []).find((f) => f.key === 'MLパターン')?.value || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(importPreview.totalCount || importPreview.data.length) > 50 && <p className="text-center text-xs text-gray-400 py-2">他 {(importPreview.totalCount || importPreview.data.length) - 50}件...</p>}
            </div>
            {importPreview.skippedList.length > 0 && (
              <details className="mb-4">
                <summary className="text-sm font-bold text-orange-600 cursor-pointer hover:underline">スキップされた {importPreview.skippedList.length}件を表示</summary>
                <div className="mt-2 max-h-40 overflow-y-auto border border-orange-200 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-orange-50 sticky top-0"><tr><th className="text-left px-3 py-2">名前</th><th className="text-left px-3 py-2">メール</th><th className="text-left px-3 py-2">理由</th></tr></thead>
                    <tbody className="divide-y">{importPreview.skippedList.map((s, i) => (<tr key={i}><td className="px-3 py-1.5">{s.name}</td><td className="px-3 py-1.5">{s.email}</td><td className="px-3 py-1.5 text-orange-600">{s.reason}</td></tr>))}</tbody>
                  </table>
                </div>
              </details>
            )}
            <div className="flex gap-3">
              <button onClick={executeImport} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'インポート中...' : `${(importPreview.totalCount || importPreview.data.length)}件をインポート`}
              </button>
              <button onClick={() => setImportPreview(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
            </div>
          </div>
        )}

        {!currentId ? (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">セミナーまたはイベントを選択してください</div>
        ) : (
          <>
            {/* 参加者へ一斉送信 */}
            <div className="bg-white rounded-xl shadow px-4 py-3 mb-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-gray-700">参加者へ一斉送信 <span className="text-xs text-gray-400 font-normal">（{members.length}名）</span></p>
                <button onClick={() => setComposeShow((v) => !v)} className="text-xs text-blue-600 font-bold hover:underline">{composeShow ? '閉じる' : '＋メール作成'}</button>
              </div>
              {composeShow && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-600">差出人</label>
                    <select value={composeFrom} onChange={(e) => setComposeFrom(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="SrWith株式会社 事務局">SrWith株式会社 事務局</option>
                      <option value="社会保険労務士 塚本祥太郎">社会保険労務士 塚本祥太郎</option>
                    </select>
                  </div>
                  <input type="text" value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="件名"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <textarea value={composeBody} onChange={(e) => setComposeBody(e.target.value)} rows={8} placeholder="本文（{company} {name} が差し込めます）"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={composeSign} onChange={(e) => setComposeSign(e.target.checked)} />
                    <span className="text-xs font-bold text-gray-700">末尾に署名を自動で付ける</span>
                    <span className="text-[11px] text-gray-400 font-normal">（本文に署名を書くならOFF）</span>
                  </label>
                  <div>
                    <button onClick={() => sendToParticipants(members.map((c) => c.email))} disabled={composeSending}
                      className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                      {composeSending ? '登録中...' : `参加者${members.length}名に送信`}
                    </button>
                    <span className="ml-2 text-[11px] text-gray-400">サーバーで送信・画面を閉じてもOK・配信停止(BL)は自動除外</span>
                  </div>
                </div>
              )}
            </div>

            {/* 参加者テーブル */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="max-h-[65vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">名前</th>
                      <th className="text-left px-3 py-2">会社名</th>
                      <th className="text-left px-3 py-2">メール</th>
                      <th className="text-left px-3 py-2">ステータス</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {members.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openEdit(c)}>
                        <td className="px-3 py-2 text-blue-600 hover:underline">{c.name}</td>
                        <td className="px-3 py-2 text-gray-500">{getField(c, '会社名')}</td>
                        <td className="px-3 py-2 text-gray-500">{c.email}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-0.5">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusColors[c.salesStatus || c.status] || 'bg-gray-100 text-gray-600'}`}>{c.salesStatus || c.status}</span>
                            {c.srwithContract === 'あり' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">SrWith</span>}
                            {c.academyStatus === '受講中' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">アカデミー</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* 編集モーダル */}
        {editForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto">
              <h3 className="font-bold text-lg mb-4">参加者情報の編集</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">姓</label>
                    <input type="text" value={(editForm.name || '').split(/\s+/)[0] || ''} onChange={(e) => {
                      const mei = (editForm.name || '').split(/\s+/).slice(1).join(' ');
                      setEditForm({ ...editForm, name: (e.target.value + ' ' + mei).trim() });
                    }} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">名</label>
                    <input type="text" value={(editForm.name || '').split(/\s+/).slice(1).join(' ') || ''} onChange={(e) => {
                      const sei = (editForm.name || '').split(/\s+/)[0] || '';
                      setEditForm({ ...editForm, name: (sei + ' ' + e.target.value).trim() });
                    }} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">メールアドレス</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">区分</label>
                    <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="company">企業</option><option value="sr">社労士</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">営業</label>
                    <select value={editForm.salesStatus || editForm.status || '見込み'} onChange={(e) => setEditForm({ ...editForm, salesStatus: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {salesStatusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold mb-1">SrWith契約</label>
                    <select value={editForm.srwithContract || 'なし'} onChange={(e) => setEditForm({ ...editForm, srwithContract: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {['なし', 'あり'].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">アカデミー</label>
                    <select value={editForm.academyStatus || '未受講'} onChange={(e) => setEditForm({ ...editForm, academyStatus: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {['未受講', '受講中', '解約済み'].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1">MLパターン</label>
                    <select value={(editForm.fields || []).find((f) => f.key === 'MLパターン')?.value || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditForm((prev) => {
                          const fields = prev.fields.filter((f) => f.key !== 'MLパターン');
                          if (val) fields.push({ key: 'MLパターン', value: val });
                          return { ...prev, fields };
                        });
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">なし</option>
                      {currentId && getEventPatterns(currentId).map(([p]) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">メモ</label>
                  <textarea value={editForm.memo} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} rows={2}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={saveEdit} disabled={editSaving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                    {editSaving ? '保存中...' : '保存'}
                  </button>
                  <button onClick={() => setEditForm(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default EventAdmin;
